import { getAccount } from '../data-access/account-query'
import { getCustomContent } from '../data-access/custom-content-query'
import { customContent_customContent as CustomContent } from '../data-access/__generated__/types'
import { getSubdomain } from '../utils/getSubdomain'

export interface TenantProps {
  accountName?: string
  accountId?: number
  windowWidth: number
  customContent?: CustomContent | null
}

// Every outcome is named, so nothing can fall through to a blank page.
export type BootstrapResult =
  | { status: 'ready'; tenant: TenantProps }
  | { status: 'redirect'; target: string }
  | { status: 'unavailable' }

export const REDIRECT_TARGETS = {
  marketing: 'https://www.120wateraudit.com',
  schools: 'https://schools.120wateraudit.com',
  pws: 'https://pws.120wateraudit.com'
} as const

// Account types, as the API returns them.
const ACCOUNT_TYPE_SCHOOLS = 1
const ACCOUNT_TYPE_PWS = 2

export async function resolveTenant(): Promise<BootstrapResult> {
  const subdomain = getSubdomain()

  let response
  try {
    response = await getAccount(subdomain)
  } catch {
    // The API is unreachable. Under Next this threw during SSR and the user got
    // an error page; in the browser it left the promise rejected and the screen blank.
    return { status: 'unavailable' }
  }

  const { data, errors } = response

  // Unchanged from the server version: an unknown subdomain goes to marketing.
  if ((errors && errors.length > 0) || !data || !data.account) {
    return { status: 'redirect', target: REDIRECT_TARGETS.marketing }
  }

  const { account } = data

  if (!account.hasPTDFeature) {
    switch (account.type) {
      case ACCOUNT_TYPE_SCHOOLS:
        return { status: 'redirect', target: REDIRECT_TARGETS.schools }
      case ACCOUNT_TYPE_PWS:
        return { status: 'redirect', target: REDIRECT_TARGETS.pws }
      default:
        return { status: 'redirect', target: REDIRECT_TARGETS.marketing }
    }
  }

  // Custom content is decoration; losing it must not cost the tenant the dashboard.
  let customContent: CustomContent | null = null
  try {
    const result = await getCustomContent(account.id)
    customContent = result.data.customContent
  } catch {
    customContent = null
  }

  return {
    status: 'ready',
    tenant: {
      accountId: account.id,
      accountName: account.name,
      windowWidth: getWindowWidth(),
      customContent
    }
  }
}

// The server derived this from the user-agent because it had no window.
const getWindowWidth = (): number =>
  typeof window !== 'undefined' ? window.innerWidth : 0
